import { Box, Typography, TextField, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { evaluateProfile } from '../../services/userService';

function Assessment({ keySkillsAndExpertise, linkedinUsername }) {
    const [evaluationText, setEvaluationText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [evaluationResult, setEvaluationResult] = useState(null);

    const handleEvaluate = async () => {
        if (!evaluationText.trim() || !linkedinUsername) {
            console.error('Missing evaluation text or LinkedIn username');
            return;
        }

        setIsLoading(true);
        try {
            const response = await evaluateProfile(linkedinUsername, evaluationText.trim());
            if (response.status === 'success') {
                setEvaluationResult(response.data);
            }
            
        } catch (error) {
            console.error('Error evaluating profile:', error);
            // Handle error response here
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ mx: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>Assessment</Typography>
                <Typography variant="body1" sx={{ color: '#888', mb: 3 }}>{keySkillsAndExpertise}</Typography>
            </Box>
            
            {/* Evaluation Form */}
            <Paper elevation={2} sx={{ p: 1, borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ color: '#666', mb: 1 }}>Evaluate on</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Enter evaluation criteria"
                        variant="outlined"
                        size="small"
                        value={evaluationText}
                        onChange={(e) => setEvaluationText(e.target.value)}
                        placeholder="e.g., leadership skills, technical expertise, communication..."
                        sx={{ width: '50%' }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleEvaluate}
                        disabled={!evaluationText.trim() || isLoading}
                        sx={{
                            bgcolor: '#1BE885',
                            color: '#000',
                            fontWeight: 600,
                            px: 2,
                            py: 1,
                            '&:hover': {
                                bgcolor: '#20ec84'
                            },
                            '&:disabled': {
                                bgcolor: '#ccc'
                            }
                        }}
                    >
                        GO →
                    </Button>
                </Box>
            </Paper>
            
            {/* Evaluation Results Table or Loading */}
            {isLoading ? (
                <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={60} sx={{ color: '#1BE885', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#666' }}>Evaluating Profile...</Typography>
                        <Typography variant="body2" sx={{ color: '#888', mt: 1 }}>Please wait while we analyze the profile</Typography>
                    </Box>
                </Paper>
            ) : evaluationResult ? (
                <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>Evaluation Results</Typography>
                    
                    {/* Skills and Ratings Table */}
                    <TableContainer sx={{ mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Functional Area</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Justification</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {evaluationResult.functional.map((skill, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{skill}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={`${evaluationResult.rating[index]}%`}
                                                sx={{
                                                    bgcolor: evaluationResult.rating[index] >= 85 ? '#4caf50' : 
                                                             evaluationResult.rating[index] >= 75 ? '#ff9800' : '#f44336',
                                                    color: 'white',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                                {evaluationResult.justifications[index]}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Additional Insights */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>Additional Insights</Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4caf50', mb: 1 }}>
                                Strengths
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
                                {evaluationResult.additional_insights.strengths}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
                                Areas for Improvement
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
                                {evaluationResult.additional_insights.improvements}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ mt: 3 }}>
                            <Chip 
                                label={`Final Recommendation: ${evaluationResult.additional_insights.final_recommendation}`}
                                sx={{
                                    bgcolor: evaluationResult.additional_insights.final_recommendation === 'Hire' ? '#4caf50' : '#f44336',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    py: 1
                                }}
                            />
                        </Box>
                    </Box>
                </Paper>
            ) : null}
        </Box>
    );
}

export default Assessment;  